import {
  HHButton,
  HHDivider,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { PropsWithChildren, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Primitives for surfaces that identify a record — the panel that heads an
 * entity detail page, and the cards that list related entities.
 *
 * Their job is to identify the record and expose the ids and links an operator
 * needs to reach related records. Three type roles do that work: a quiet
 * label, a readable value, and a tabular id. Hierarchy comes from size and
 * colour alone — sentence case throughout, one typeface, no letter-spacing or
 * case transforms, so these surfaces match the rest of the tool rather than
 * introducing a style of their own. Ids are right-aligned in tabular figures
 * so they form a single scannable column — they are what operators copy into
 * queries.
 */

const LABEL_STYLES = {
  fontSize: 12,
  color: 'text.secondary',
  lineHeight: 1.5,
} as const;

const ID_STYLES = {
  fontSize: 12,
  // Tabular figures keep the id column aligned without introducing a second
  // typeface — the theme's Roboto stays throughout.
  fontVariantNumeric: 'tabular-nums',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  lineHeight: 1.5,
} as const;

export const RecordPanelLabel = ({
  children,
}: PropsWithChildren): JSX.Element => (
  <HHTypography hhVariant="variant-bypass" variant="body2" sx={LABEL_STYLES}>
    {children}
  </HHTypography>
);

export const RecordPanelId = ({ children }: PropsWithChildren): JSX.Element => (
  <HHTypography hhVariant="variant-bypass" variant="body2" sx={ID_STYLES}>
    {children}
  </HHTypography>
);

export interface RecordPanelTitleProps {
  /**
   * Route to the record's own page. Omit when the panel already heads that
   * page — the title then reads as plain text rather than a link, so green
   * stays reserved for things you can navigate to.
   */
  to?: string;
  testId?: string;
  children: ReactNode;
}

export const RecordPanelTitle = ({
  to,
  testId,
  children,
}: RecordPanelTitleProps): JSX.Element => {
  // Plain h2 — the same heading style the tool uses for section titles, with
  // no size or weight overrides, so record titles read as part of the set.
  const title = (
    <HHTypography
      hhVariant="variant-bypass"
      variant="h2"
      sx={{
        overflowWrap: 'anywhere',
        color: to ? 'primary.main' : 'text.primary',
        ...(to ? { '&:hover': { textDecoration: 'underline' } } : {}),
      }}
    >
      {children}
    </HHTypography>
  );

  if (!to) {
    return title;
  }

  return (
    <Link to={to} style={{ textDecoration: 'none' }} data-testid={testId}>
      {title}
    </Link>
  );
};

export interface RecordPanelMetaProps {
  label: string;
  value: string;
}

/**
 * Inline metadata about the headline record itself, for the line that carries
 * the status.
 */
export const RecordPanelMeta = ({
  label,
  value,
}: RecordPanelMetaProps): JSX.Element => (
  <HHStack direction="row" alignItems="baseline" gap={1}>
    <RecordPanelLabel>{label}</RecordPanelLabel>
    <RecordPanelId>{value}</RecordPanelId>
  </HHStack>
);

export interface RecordPanelProps {
  testId?: string;
}

/** Fixed-width column that sits to the left of a page's main section. */
export const RecordPanel = ({
  testId,
  children,
}: PropsWithChildren<RecordPanelProps>): JSX.Element => (
  <HHStack
    direction="column"
    padding={4}
    data-testid={testId}
    sx={(theme): SystemStyleObject<Theme> => ({
      // Clamped rather than a bare percentage so the panel stays readable on
      // laptop widths, where 18% left it around 128px.
      width: 'clamp(216px, calc(18% - 40px), 272px)',
      flexShrink: 0,
      overflowY: 'auto',
      backgroundColor: theme.palette.background.default,
      borderRight: `1px solid ${theme.palette.divider}`,
    })}
  >
    {children}
  </HHStack>
);

export interface RecordPanelHeadingProps {
  /** Entity type of the headline record, e.g. "Client". */
  label: string;
  /** Id of the headline record, already prefixed — e.g. "Id 22". */
  id: string;
  /** The record name. Pass a link when the record has its own page. */
  children: ReactNode;
}

export const RecordPanelHeading = ({
  label,
  id,
  children,
}: RecordPanelHeadingProps): JSX.Element => (
  <HHStack direction="column">
    <HHStack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      gap={1}
    >
      <RecordPanelLabel>{label}</RecordPanelLabel>
      <RecordPanelId>{id}</RecordPanelId>
    </HHStack>
    {children}
  </HHStack>
);

/**
 * Other pages in the tool that hold this record's data — its bills, its
 * contracts. A list rather than a button cluster, so it keeps reading as part
 * of the panel's information structure.
 *
 * Icons lead rather than trail: the panel's right edge is the id column, and a
 * trailing chevron would compete with it.
 */
export const RecordPanelDestinations = ({
  children,
}: PropsWithChildren): JSX.Element => (
  <HHStack direction="column" marginTop={6}>
    <HHDivider sx={{ marginBottom: 3 }} />
    {children}
  </HHStack>
);

export interface RecordPanelDestinationProps {
  testId: string;
  icon: ReactNode;
  /**
   * Opened in a new tab — these are lookups you take *while* working the
   * record, so the panel should still be there when you come back. A real
   * href rather than a click handler, so cmd-click and middle-click work and
   * the row is announced as a link.
   */
  href: string;
  /** Name the destination, not the trip — these are nav rows, so "Bills". */
  children: ReactNode;
}

export const RecordPanelDestination = ({
  testId,
  icon,
  href,
  children,
}: RecordPanelDestinationProps): JSX.Element => (
  <HHButton
    hhVariant="variant-bypass"
    variant="text"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    startIcon={icon}
    sx={{
      justifyContent: 'flex-start',
      // Negative inset lets the hover tint reach past the panel's padding
      // while the label still lines up with the field labels above it.
      marginX: -2,
      paddingX: 2,
      paddingY: 1,
      minWidth: 0,
      fontSize: 13,
      textTransform: 'none',
      // The record title above is already a green link. Leaving the row label
      // in body colour keeps one primary in the panel; the icon and the hover
      // state carry the affordance.
      color: 'text.primary',
      '& .MuiButton-startIcon': {
        color: 'primary.main',
        marginLeft: 0,
        marginRight: 2,
      },
      '&:hover': { color: 'primary.main' },
    }}
  >
    {children}
  </HHButton>
);

export interface RecordPanelExternalLinkProps {
  testId: string;
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
}

/**
 * An escape hatch to a different system, pinned to the panel's bottom edge and
 * muted — it is not one of the tool's own destinations and shouldn't sit in
 * that list.
 */
export const RecordPanelExternalLink = ({
  testId,
  icon,
  onClick,
  children,
}: RecordPanelExternalLinkProps): JSX.Element => (
  <HHStack direction="column" marginTop="auto" paddingTop={6}>
    <HHDivider sx={{ marginBottom: 3 }} />
    <HHButton
      hhVariant="variant-bypass"
      variant="text"
      size="small"
      data-testid={testId}
      startIcon={icon}
      sx={{
        alignSelf: 'flex-start',
        padding: 0,
        minWidth: 0,
        fontSize: 12,
        textTransform: 'none',
        color: 'text.secondary',
        '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 2 },
        '&:hover': { color: 'text.primary', backgroundColor: 'transparent' },
      }}
      onClick={onClick}
    >
      {children}
    </HHButton>
  </HHStack>
);

export interface RecordPanelFieldProps {
  label: string;
  /** Id of the related record, already prefixed — e.g. "Id 33". */
  id?: string;
  value: ReactNode;
}

/** A related record: label and id on one line, name on the next. */
export const RecordPanelField = ({
  label,
  id,
  value,
}: RecordPanelFieldProps): JSX.Element => (
  <HHStack direction="column">
    <HHStack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      gap={1}
    >
      <RecordPanelLabel>{label}</RecordPanelLabel>
      {id ? <RecordPanelId>{id}</RecordPanelId> : null}
    </HHStack>
    <HHTypography
      hhVariant="variant-bypass"
      variant="body1"
      sx={{ overflowWrap: 'anywhere', marginTop: '2px' }}
    >
      {value}
    </HHTypography>
  </HHStack>
);
